/**
 * Compliance Automation Dashboard
 * 
 * Displays compliance guardrails status including:
 * - Aide supervision tracking (14-day cycle)
 * - LPN supervision tracking (30-day cycle)
 * - Medication reconciliation status
 * - Active alerts and escalations
 * - QAPI metrics
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { ScrollArea } from "./ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Separator } from "./ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  ClipboardCheck,
  FileText,
  TrendingUp,
  RefreshCw,
  ChevronRight,
  Bell,
  Activity,
  Calendar
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

const STATUS_BADGES = {
  current: { color: "bg-green-500/20 text-green-300", icon: CheckCircle, label: "Current" },
  due_soon: { color: "bg-amber-500/20 text-amber-300", icon: Clock, label: "Due Soon" },
  overdue: { color: "bg-red-500/20 text-red-300", icon: AlertTriangle, label: "Overdue" },
  no_aide_assigned: { color: "bg-slate-500/20 text-slate-300", icon: Users, label: "N/A" },
  no_lpn_assigned: { color: "bg-slate-500/20 text-slate-300", icon: Users, label: "N/A" }
};

export function ComplianceDashboard({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [supervisionData, setSupervisionData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [rules, setRules] = useState({});
  const [qapiReport, setQapiReport] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [supervisionRes, alertsRes, rulesRes, qapiRes] = await Promise.all([
        axios.get(`${API_URL}/api/compliance-automation/supervision/dashboard`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/compliance-automation/alerts?status=active`).catch(() => ({ data: { alerts: [] } })),
        axios.get(`${API_URL}/api/compliance-automation/rules`).catch(() => ({ data: { rules: {} } })),
        axios.get(`${API_URL}/api/compliance-automation/qapi-report`).catch(() => ({ data: {} }))
      ]);
      
      setSupervisionData(supervisionRes.data);
      setAlerts(alertsRes.data.alerts || []);
      setRules(rulesRes.data.rules || {});
      setQapiReport(qapiRes.data);
    } catch (err) {
      console.error("Failed to fetch compliance data:", err);
    } finally {
      setLoading(false);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await axios.post(`${API_URL}/api/compliance-automation/alerts/${alertId}/acknowledge`, null, {
        params: { acknowledged_by: "current_user" }
      });
      toast.success("Alert acknowledged");
      fetchData();
    } catch (err) {
      toast.error("Failed to acknowledge alert");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-teal-400" />
        <span className="ml-2 text-slate-400">Loading compliance data...</span>
      </div>
    );
  }

  const summary = supervisionData?.summary || {};
  const aideCompliance = supervisionData?.compliance_rate?.aide || 100;
  const lpnCompliance = supervisionData?.compliance_rate?.lpn || 100;

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500/20 to-teal-500/20">
            <Shield className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Compliance Automation</h2>
            <p className="text-xs text-slate-400">CMS CoP Guardrails & Supervision Tracking</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold text-emerald-400">{aideCompliance}%</div>
            <p className="text-xs text-slate-400">Aide Supervision</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold text-emerald-400">{lpnCompliance}%</div>
            <p className="text-xs text-slate-400">LPN Supervision</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-3 text-center">
            <div className={`text-2xl font-bold ${(summary.aide_overdue || 0) + (summary.lpn_overdue || 0) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
              {(summary.aide_overdue || 0) + (summary.lpn_overdue || 0)}
            </div>
            <p className="text-xs text-slate-400">Overdue</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-3 text-center">
            <div className={`text-2xl font-bold ${alerts.length > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
              {alerts.length}
            </div>
            <p className="text-xs text-slate-400">Active Alerts</p>
          </CardContent>
        </Card>
      </div>

      {/* Alert Banner */}
      {(summary.aide_overdue > 0 || summary.lpn_overdue > 0) && (
        <Alert className="bg-red-500/10 border-red-500/30">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <AlertTitle className="text-red-400">Supervision Overdue</AlertTitle>
          <AlertDescription className="text-red-300">
            {summary.aide_overdue > 0 && `${summary.aide_overdue} aide supervision(s) overdue. `}
            {summary.lpn_overdue > 0 && `${summary.lpn_overdue} LPN supervision(s) overdue. `}
            Immediate action required per Medicare CoP.
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="aide">Aide Supervision</TabsTrigger>
          <TabsTrigger value="lpn">LPN Supervision</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
          <TabsTrigger value="qapi">QAPI Report</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Compliance Rules */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4 text-teal-400" />
                  Compliance Rules
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(rules).slice(0, 5).map(([key, rule]) => (
                    <div key={key} className="flex items-center justify-between p-2 bg-slate-900 rounded text-sm">
                      <span className="text-slate-300">{rule.description?.slice(0, 40)}...</span>
                      {rule.cycle_days && (
                        <Badge className="bg-teal-500/20 text-teal-300">{rule.cycle_days} days</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Active Alerts Summary */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Bell className="w-4 h-4 text-amber-400" />
                  Active Alerts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {alerts.length === 0 ? (
                  <div className="text-center py-4 text-slate-500">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                    <p>No active compliance alerts</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[150px]">
                    <div className="space-y-2">
                      {alerts.slice(0, 5).map((alert) => (
                        <div key={alert.id} className="p-2 bg-slate-900 rounded border-l-2 border-amber-500">
                          <p className="text-sm font-medium">{alert.title}</p>
                          <p className="text-xs text-slate-500">{alert.patient_name}</p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Compliance Gauges */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Compliance Rates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm">Aide Supervision (14-day)</span>
                    <span className="text-sm text-emerald-400">{aideCompliance}%</span>
                  </div>
                  <Progress value={aideCompliance} className="h-2" />
                  <p className="text-xs text-slate-500 mt-1">
                    {summary.total_aide_patients || 0} patients • {summary.aide_current || 0} current • {summary.aide_due_soon || 0} due soon • {summary.aide_overdue || 0} overdue
                  </p>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm">LPN Supervision (30-day)</span>
                    <span className="text-sm text-emerald-400">{lpnCompliance}%</span>
                  </div>
                  <Progress value={lpnCompliance} className="h-2" />
                  <p className="text-xs text-slate-500 mt-1">
                    {summary.total_lpn_patients || 0} patients • {summary.lpn_current || 0} current • {summary.lpn_due_soon || 0} due soon • {summary.lpn_overdue || 0} overdue
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aide Supervision Tab */}
        <TabsContent value="aide">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Users className="w-4 h-4" />
                Aide Supervision Tracking (Every 14 Days)
              </CardTitle>
              <CardDescription>
                42 CFR 418.76(h) - RN supervisory visit required every 14 days
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(supervisionData?.aide_supervision || []).length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No aide assignments found</p>
                </div>
              ) : (
                <ScrollArea className="h-[300px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead>Patient</TableHead>
                        <TableHead>Supervising RN</TableHead>
                        <TableHead>Last Supervision</TableHead>
                        <TableHead>Days Since</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Next Due</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(supervisionData?.aide_supervision || []).map((item) => {
                        const statusConfig = STATUS_BADGES[item.status] || STATUS_BADGES.current;
                        const StatusIcon = statusConfig.icon;
                        
                        return (
                          <TableRow key={item.id} className="border-slate-700">
                            <TableCell className="font-medium">{item.patient_name}</TableCell>
                            <TableCell>{item.supervising_rn}</TableCell>
                            <TableCell>{formatDate(item.last_supervision_date)}</TableCell>
                            <TableCell>
                              <span className={item.days_since_last > 14 ? "text-red-400" : item.days_since_last > 12 ? "text-amber-400" : ""}>
                                {item.days_since_last} days
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge className={statusConfig.color}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                            </TableCell>
                            <TableCell>{formatDate(item.next_due_date)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* LPN Supervision Tab */}
        <TabsContent value="lpn">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="w-4 h-4" />
                LPN Supervision Tracking (Every 30 Days)
              </CardTitle>
              <CardDescription>
                42 CFR 418.74 - RN supervisory visit required every 30 days for LPNs
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(supervisionData?.lpn_supervision || []).length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No LPN assignments found</p>
                </div>
              ) : (
                <ScrollArea className="h-[300px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead>Patient</TableHead>
                        <TableHead>Supervising RN</TableHead>
                        <TableHead>Last Supervision</TableHead>
                        <TableHead>Days Since</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Next Due</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(supervisionData?.lpn_supervision || []).map((item) => {
                        const statusConfig = STATUS_BADGES[item.status] || STATUS_BADGES.current;
                        const StatusIcon = statusConfig.icon;
                        
                        return (
                          <TableRow key={item.id} className="border-slate-700">
                            <TableCell className="font-medium">{item.patient_name}</TableCell>
                            <TableCell>{item.supervising_rn}</TableCell>
                            <TableCell>{formatDate(item.last_supervision_date)}</TableCell>
                            <TableCell>
                              <span className={item.days_since_last > 30 ? "text-red-400" : item.days_since_last > 25 ? "text-amber-400" : ""}>
                                {item.days_since_last} days
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge className={statusConfig.color}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                            </TableCell>
                            <TableCell>{formatDate(item.next_due_date)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Compliance Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-emerald-400" />
                  <p className="font-medium text-emerald-400">All Clear!</p>
                  <p className="text-sm">No active compliance alerts</p>
                </div>
              ) : (
                <ScrollArea className="h-[300px]">
                  <div className="space-y-3">
                    {alerts.map((alert) => (
                      <div
                        key={alert.id}
                        className={`p-3 rounded border-l-4 ${
                          alert.severity === 'hard' || alert.severity === 'critical'
                            ? 'bg-red-500/10 border-red-500'
                            : 'bg-amber-500/10 border-amber-500'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{alert.title}</p>
                            <p className="text-sm text-slate-400">{alert.patient_name}</p>
                            <p className="text-sm mt-1">{alert.message}</p>
                            <p className="text-xs text-slate-500 mt-2">{alert.compliance_citation}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => acknowledgeAlert(alert.id)}
                          >
                            Acknowledge
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* QAPI Report Tab */}
        <TabsContent value="qapi">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                QAPI Compliance Report
              </CardTitle>
              <CardDescription>
                Survey-ready compliance documentation
              </CardDescription>
            </CardHeader>
            <CardContent>
              {qapiReport ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <Card className="bg-slate-900">
                      <CardContent className="p-3 text-center">
                        <div className="text-xl font-bold text-emerald-400">
                          {qapiReport.executive_summary?.aide_supervision_compliance}
                        </div>
                        <p className="text-xs text-slate-500">Aide Supervision</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-900">
                      <CardContent className="p-3 text-center">
                        <div className="text-xl font-bold text-emerald-400">
                          {qapiReport.executive_summary?.lpn_supervision_compliance}
                        </div>
                        <p className="text-xs text-slate-500">LPN Supervision</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-900">
                      <CardContent className="p-3 text-center">
                        <div className="text-xl font-bold text-emerald-400">
                          {qapiReport.executive_summary?.alerts_resolved_rate}
                        </div>
                        <p className="text-xs text-slate-500">Alerts Resolved</p>
                      </CardContent>
                    </Card>
                  </div>

                  <Separator className="bg-slate-700" />

                  <div>
                    <h4 className="text-sm font-medium mb-2">Action Items</h4>
                    <div className="space-y-2">
                      {qapiReport.action_items?.map((item, idx) => (
                        <div key={idx} className={`p-2 rounded ${
                          item.priority === 'HIGH' ? 'bg-red-500/10 border-l-2 border-red-500' : 'bg-slate-900'
                        }`}>
                          <div className="flex items-center gap-2">
                            <Badge className={item.priority === 'HIGH' ? 'bg-red-500/20 text-red-300' : 'bg-slate-500/20'}>
                              {item.priority}
                            </Badge>
                            <span className="text-sm">{item.item}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{item.recommendation}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Separator className="bg-slate-700" />

                  <div>
                    <h4 className="text-sm font-medium mb-2">Medicare CoP Citations</h4>
                    <div className="text-xs text-slate-400 space-y-1">
                      {qapiReport.medicare_citations?.map((citation, idx) => (
                        <p key={idx}>• {citation}</p>
                      ))}
                    </div>
                  </div>

                  <p className="text-xs text-slate-500 text-center mt-4">
                    Report generated: {new Date(qapiReport.generated_at).toLocaleString()}
                  </p>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Report data unavailable</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default ComplianceDashboard;
